/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Grid, Box, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  CheckCircle, Cancel, HourglassEmpty, ReportProblem,
  Visibility as EyeIcon, PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Chart, BarElement, ArcElement, CategoryScale, LinearScale } from 'chart.js';

import api from '../api';
import { getRoleFromToken } from '../utils/auth';

/* ─── Chart.js ──────────────────────────────────────────────── */
Chart.register(BarElement, ArcElement, CategoryScale, LinearScale);
const chartToJpeg = (type, data, options = {}, width = 600, height = 300) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  // eslint-disable-next-line no-new
  new Chart(canvas.getContext('2d'), { type, data, options });
  return canvas.toDataURL('image/jpeg', 1.0);
};

/* ─── helpers ──────────────────────────────────────────────── */
const levelLabel = (lvl) =>
  lvl === 1 ? 'Easy' : lvl === 2 ? 'Medium' : lvl === 3 ? 'Difficult' : '—';

/* ──────────────────────────────────────────────────────────── */
function AdminDashboard() {
  const navigate = useNavigate();

  /* loading bar */
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setProgress((p) => (p >= 90 ? p : p + 10)), 200);
    return () => clearInterval(t);
  }, [loading]);

  /* server data */
  const [questions,   setQuestions]   = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [qaList,      setQaList]      = useState([]);
  const [sessions,    setSessions]    = useState([]);
  const [pagination,  setPagination]  = useState({ total: 0, page: 1, limit: 20, pages: 0 });
  const [stats,       setStats]       = useState({ submitted: 0, approved: 0, rejected: 0, review: 0 });

  /* filters */
  const [teacherFilter, setTeacherFilter] = useState('All');
  const [qaFilter,      setQaFilter]      = useState('All');
  const [subjectFilter, setSubjectFilter] = useState('All');
  const [statusFilter,  setStatusFilter]  = useState('All');
  const [levelFilter,   setLevelFilter]   = useState('All');
  const [sessionFilter, setSessionFilter] = useState('All');

  const subjectList = [
    'All','Understanding of Holy Quran','Mathematics','Philosophy', 'Philosophy 1','English','Computer','Computer Science','IT','Physics',
    'Chemistry','Ethics','General Knowledge','General Science', 'Islamiat','Medical','Education','Business Administration','Commerce','Research/ORIC','Finance','Examinations','Civil','Electrical Engineering','Security','QEC'
  ];
  const statusList = ['All','draft','submitted','approved','rejected','review_needed'];

  /* UI feedback */
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  /* summary counts */
  const [submittedCount, setSubmitted] = useState(0);
  const [approvedCount,  setApproved]  = useState(0);
  const [rejectedCount,  setRejected]  = useState(0);
  const [reviewCount,    setReview]    = useState(0);

  /* modals */
  const [openView,     setOpenView]     = useState(false);
  const [openSubModal, setOpenSubModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const isSuperAdmin = currentRole === 'superadmin';

  /* fetch on mount */
  useEffect(() => {
    (async () => {
      try {
        const [qRes, tRes, qaRes, sRes, statsRes] = await Promise.all([
          api.get('/api/dashboard/questions?page=1&limit=20'),
          api.get('/api/users/teacherList'),
          api.get('/api/users/qaList'),
          api.get('/api/sessions'),
          api.get('/api/dashboard/stats'),
        ]);
        setQuestions(qRes.data.questions || qRes.data);
        setPagination(qRes.data.pagination || { total: 0, page: 1, limit: 20, pages: 0 });
        setTeacherList(tRes.data);
        setQaList(qaRes.data);
        setSessions(sRes.data);
        
        // Set initial stats
        if (statsRes.data) {
          setStats({
            submitted: statsRes.data.submitted || 0,
            approved: statsRes.data.approved || 0,
            rejected: statsRes.data.rejected || 0,
            review: statsRes.data.review || 0
          });
        }
        
        setProgress(100);
        setTimeout(() => setLoading(false), 400);
      } catch {
        setError('Failed to load dashboard data.');
        setLoading(false);
      }
    })();
    // determine current role from token
    try {
      const r = getRoleFromToken();
      setCurrentRole(r);
    } catch (err) {
      setCurrentRole(null);
    }
  }, []);

  /* fetch questions with pagination and all filters */
  const fetchQuestions = async (page = 1) => {
    try {
      setFilterLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (sessionFilter && sessionFilter !== 'All') {
        params.append('sessionId', sessionFilter);
      }
      if (teacherFilter && teacherFilter !== 'All') {
        params.append('teacherId', teacherFilter);
      }
      if (qaFilter && qaFilter !== 'All') {
        params.append('qaId', qaFilter);
      }
      if (subjectFilter && subjectFilter !== 'All') {
        params.append('subject', subjectFilter);
      }
      if (statusFilter && statusFilter !== 'All') {
        params.append('status', statusFilter);
      }
      if (levelFilter && levelFilter !== 'All') {
        params.append('level', levelFilter);
      }
      
      const [qRes, statsRes] = await Promise.all([
        api.get(`/api/dashboard/questions?${params.toString()}`),
        api.get('/api/dashboard/stats')
      ]);
      
      setQuestions(qRes.data.questions || qRes.data);
      setPagination(qRes.data.pagination || { total: 0, page, limit: 20, pages: 0 });
      
      // Set stats from backend
      if (statsRes.data) {
        setStats({
          submitted: statsRes.data.submitted || 0,
          approved: statsRes.data.approved || 0,
          rejected: statsRes.data.rejected || 0,
          review: statsRes.data.review || 0
        });
      }
      
      setFilterLoading(false);
    } catch {
      setError('Failed to load questions.');
      setFilterLoading(false);
    }
  };

  /* refetch when any filter changes */
  useEffect(() => {
    if (!loading) {
      fetchQuestions(1);
    }
  }, [sessionFilter, teacherFilter, qaFilter, subjectFilter, statusFilter, levelFilter]);


  /* rows - use questions directly from backend (already filtered) */
  const rows = questions;

  /* columns */
  const columns = [
    {
      field: 'questionSnippet',
      headerName: 'Question',
      flex: 3,
      minWidth: 250,
      valueGetter: ({ row }) => row?.question || '',          // guard #1
      renderCell: ({ row }) => {
        const fullText = (row?.question || '').replace(/<[^>]+>/g, '');
        const words = fullText.trim().split(/\s+/);
        const snippetText = isSuperAdmin 
          ? fullText.slice(0, 60) || '…'
          : (words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : ''));
        return (
          <Box>
            {row?.subQuestions?.length > 0 && (
              <Box sx={{ display:'inline-block', bgcolor:'cyan', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.7rem' }}>SubQ</Box>
            )}
            {row?.question?.includes('<img') && (
              <Box sx={{ display:'inline-block', bgcolor:'#ffcb00', borderRadius:1, px:0.5, mr:0.5, fontSize:'0.7rem' }}>Img</Box>
            )}
            <span>{snippetText}</span>
          </Box>
        );
      },
    },
    { field: 'subject', headerName: 'Subject', width: 110 },
{
  field: 'level',
  headerName: 'Level',
  width: 90,
  renderCell: ({ row }) => levelLabel(row?.level),
},
    { field: 'teacherName', headerName: 'Teacher',     width: 110 },
    { field: 'qaName',      headerName: 'Assigned QA', width: 120 },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 120,
      valueGetter: ({ row }) => {
        const dateVal = row?.dateCreated;
        if (!dateVal) return null;
        return dateVal;
      },
      renderCell: ({ row }) => {
        const dateVal = row?.dateCreated;
        if (!dateVal) return '—';
        try {
          const date = new Date(dateVal);
          if (isNaN(date.getTime())) return '—';
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric' 
          });
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => {
        const palette = { draft:'#888', submitted:'orange', approved:'green', rejected:'red', review_needed:'blue' };
        const label = value === 'review_needed' ? 'Review Needed' : value.charAt(0).toUpperCase()+value.slice(1);
        return (
          <Box sx={{ bgcolor:palette[value], color:'#fff', borderRadius:1, px:1, py:0.25, width:'100%', textAlign:'center' }}>
            {label}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" variant="outlined" startIcon={<EyeIcon />} onClick={() => handleView(row)}>
          View
        </Button>
      ),
    },
  ];

  /* ───────── PDF report ───────── */
  const buildPdf = async () => {
    try {
      // Fetch ALL questions for the PDF (no pagination limit)
      setError('');
      setFilterLoading(true);
      
      const response = await api.get('/api/dashboard/questions?page=1&limit=999999');
      const allQuestions = response.data.questions || [];
      
      setFilterLoading(false);
      
      if (allQuestions.length === 0) {
        setError('No questions available to generate PDF report.');
        return;
      }

      const doc = new jsPDF({ orientation: 'landscape' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const statusCols = [
        'draft',
        'submitted',
        'approved',
        'rejected',
        'review_needed',
      ];
      const levelCols = [1, 2, 3];

    // Helper function to draw modern header
    const drawHeader = (pageTitle = '', showLogo = true) => {
      // Purple gradient background effect (simulated with rectangles)
      doc.setFillColor(102, 126, 234); // #667eea
      doc.rect(0, 0, pageW, 35, 'F');
      
      // Logo/Brand
      if (showLogo) {
        doc.setFontSize(24);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('CBT PORTAL', 20, 15);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Computer-Based Testing System', 20, 21);
      }
      
      // Page title
      if (pageTitle) {
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(pageTitle, pageW - 20, 20, { align: 'right' });
      }
      
      // Date and time
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(240, 240, 255);
      doc.text(`Generated: ${new Date().toLocaleString('en-US', { 
        dateStyle: 'medium', 
        timeStyle: 'short' 
      })}`, pageW - 20, 28, { align: 'right' });
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
    };

    // Helper function to draw footer
    const drawFooter = (pageNum, totalPages) => {
      doc.setDrawColor(102, 126, 234);
      doc.setLineWidth(0.5);
      doc.line(20, pageH - 15, pageW - 20, pageH - 15);
      
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text('Confidential - For Internal Use Only', 20, pageH - 10);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${pageNum} of ${totalPages}`, pageW / 2, pageH - 10, { align: 'center' });
      doc.text('CBT Portal © 2025', pageW - 20, pageH - 10, { align: 'right' });
      
      doc.setTextColor(0, 0, 0);
    };

    // Helper to draw a card/section
    const drawCard = (x, y, width, height, title, color = [102, 126, 234]) => {
      // Card shadow effect
      doc.setFillColor(220, 220, 220);
      doc.roundedRect(x + 1, y + 1, width, height, 2, 2, 'F');
      
      // Card background
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, width, height, 2, 2, 'FD');
      
      // Card header
      doc.setFillColor(...color);
      doc.roundedRect(x, y, width, 8, 2, 2, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(title, x + 4, y + 5.5);
      
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    };

    /* ═══════════════════════════════════════════════════════════
       PAGE 1: EXECUTIVE SUMMARY
       ═══════════════════════════════════════════════════════════ */
    
    drawHeader('Executive Summary', true);

    // Calculate statistics
    const overallStatus = statusCols.map(
      (s) => allQuestions.filter((q) => q.status === s).length,
    );
    const overallLevels = levelCols.map(
      (l) => allQuestions.filter((q) => q.level === l).length,
    );

    const totalQuestions = allQuestions.length;
    const approvalRate = totalQuestions > 0 
      ? ((overallStatus[2] / totalQuestions) * 100).toFixed(1) 
      : 0;

    // Key Metrics Cards
    const cardY = 45;
    const cardWidth = 55;
    const cardHeight = 28;
    const cardSpacing = 6;

    // Total Questions Card
    drawCard(20, cardY, cardWidth, cardHeight, 'Total Questions', [102, 126, 234]);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(102, 126, 234);
    doc.text(totalQuestions.toString(), 20 + cardWidth / 2, cardY + 20, { align: 'center' });
    
    // Approved Card
    drawCard(20 + cardWidth + cardSpacing, cardY, cardWidth, cardHeight, 'Approved', [76, 175, 80]);
    doc.setFontSize(28);
    doc.setTextColor(76, 175, 80);
    doc.text(overallStatus[2].toString(), 20 + cardWidth + cardSpacing + cardWidth / 2, cardY + 20, { align: 'center' });
    
    // Pending Review Card
    drawCard(20 + (cardWidth + cardSpacing) * 2, cardY, cardWidth, cardHeight, 'Pending Review', [33, 150, 243]);
    doc.setFontSize(28);
    doc.setTextColor(33, 150, 243);
    doc.text(overallStatus[4].toString(), 20 + (cardWidth + cardSpacing) * 2 + cardWidth / 2, cardY + 20, { align: 'center' });
    
    // Approval Rate Card
    drawCard(20 + (cardWidth + cardSpacing) * 3, cardY, cardWidth, cardHeight, 'Approval Rate', [156, 39, 176]);
    doc.setFontSize(28);
    doc.setTextColor(156, 39, 176);
    doc.text(`${approvalRate}%`, 20 + (cardWidth + cardSpacing) * 3 + cardWidth / 2, cardY + 20, { align: 'center' });

    // Status Distribution Section
    const statusCardY = cardY + cardHeight + 10;
    drawCard(20, statusCardY, 125, 65, 'Question Status Distribution', [102, 126, 234]);

    // Pie chart for status
    const pieUrl = chartToJpeg(
      'doughnut',
      {
        labels: ['Draft', 'Submitted', 'Approved', 'Rejected', 'Review Needed'],
        datasets: [
          {
            data: overallStatus,
            backgroundColor: [
              '#9E9E9E',
              '#FF9800',
              '#4CAF50',
              '#F44336',
              '#2196F3',
            ],
            borderWidth: 2,
            borderColor: '#ffffff',
          },
        ],
      },
      { 
        plugins: { 
          legend: { 
            display: true,
            position: 'right',
            labels: { 
              boxWidth: 12,
              font: { size: 9 },
              padding: 8
            }
          } 
        },
        cutout: '60%'
      },
      400,
      200,
    );
    doc.addImage(pieUrl, 'JPEG', 22, statusCardY + 10, 52, 30);

    // Status table
    autoTable(doc, {
      startY: statusCardY + 10,
      margin: { left: 80 },
      tableWidth: 60,
      head: [['Status', 'Count', '%']],
      body: [
        ['Draft', overallStatus[0], totalQuestions ? ((overallStatus[0] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Submitted', overallStatus[1], totalQuestions ? ((overallStatus[1] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Approved', overallStatus[2], totalQuestions ? ((overallStatus[2] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Rejected', overallStatus[3], totalQuestions ? ((overallStatus[3] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Review Needed', overallStatus[4], totalQuestions ? ((overallStatus[4] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
      ],
      theme: 'striped',
      headStyles: { 
        fillColor: [102, 126, 234],
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: { 
        fontSize: 9,
        cellPadding: 2
      },
      alternateRowStyles: { fillColor: [245, 245, 250] },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { halign: 'center', cellWidth: 15 },
        2: { halign: 'center', cellWidth: 15, textColor: [102, 126, 234], fontStyle: 'bold' }
      }
    });

    // Level Distribution Section
    drawCard(155, statusCardY, 125, 65, 'Difficulty Level Distribution', [76, 175, 80]);

    // Bar chart for levels
    const levelBarUrl = chartToJpeg(
      'bar',
      {
        labels: ['Easy', 'Medium', 'Difficult'],
        datasets: [
          {
            label: 'Questions',
            data: overallLevels,
            backgroundColor: [
              '#4CAF50',
              '#FF9800',
              '#F44336',
            ],
            borderRadius: 4,
          },
        ],
      },
      {
        plugins: { 
          legend: { display: false }
        },
        scales: { 
          y: { 
            beginAtZero: true, 
            ticks: { precision: 0, font: { size: 9 } },
            grid: { color: '#f0f0f0' }
          },
          x: {
            ticks: { font: { size: 9 } },
            grid: { display: false }
          }
        }
      },
      400,
      200,
    );
    doc.addImage(levelBarUrl, 'JPEG', 157, statusCardY + 10, 55, 30);

    // Level table
    autoTable(doc, {
      startY: statusCardY + 10,
      margin: { left: 220 },
      tableWidth: 55,
      head: [['Level', 'Count', '%']],
      body: [
        ['Easy (1)', overallLevels[0], totalQuestions ? ((overallLevels[0] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Medium (2)', overallLevels[1], totalQuestions ? ((overallLevels[1] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
        ['Difficult (3)', overallLevels[2], totalQuestions ? ((overallLevels[2] / totalQuestions) * 100).toFixed(1) + '%' : '0%'],
      ],
      theme: 'striped',
      headStyles: { 
        fillColor: [76, 175, 80],
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: { 
        fontSize: 9,
        cellPadding: 2
      },
      alternateRowStyles: { fillColor: [245, 250, 245] },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { halign: 'center', cellWidth: 13 },
        2: { halign: 'center', cellWidth: 14, textColor: [76, 175, 80], fontStyle: 'bold' }
      }
    });

    /* Teacher summary table */
    const teacherAgg = {};
    allQuestions.forEach((q) => {
      const t = q.teacherName || q.teacherId || 'Unknown';
      teacherAgg[t] ??= { total: 0 };
      teacherAgg[t][q.status] = (teacherAgg[t][q.status] || 0) + 1;
      teacherAgg[t][`L${q.level}`] =
        (teacherAgg[t][`L${q.level}`] || 0) + 1;
      teacherAgg[t].total += 1;
    });

    // Teacher Summary Section
    const teacherTableY = statusCardY + 75;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(102, 126, 234);
    doc.text('📊 Teacher Performance Summary', 20, teacherTableY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: teacherTableY + 5,
      margin: { left: 20, right: 20 },
      head: [
        [
          'Teacher Name',
          'Draft',
          'Submitted',
          'Approved',
          'Rejected',
          'Review',
          'Easy',
          'Medium',
          'Hard',
          'Total',
        ],
      ],
      body: Object.entries(teacherAgg).map(([t, v]) => [
        t,
        v.draft || 0,
        v.submitted || 0,
        v.approved || 0,
        v.rejected || 0,
        v.review_needed || 0,
        v.L1 || 0,
        v.L2 || 0,
        v.L3 || 0,
        v.total,
      ]),
      theme: 'grid',
      headStyles: { 
        fillColor: [102, 126, 234],
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: { 
        fontSize: 8,
        cellPadding: 2
      },
      alternateRowStyles: { fillColor: [250, 250, 255] },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold' },
        9: { fillColor: [240, 240, 255], fontStyle: 'bold', halign: 'center', textColor: [102, 126, 234] }
      },
      didParseCell: (data) => {
        // Color code status columns
        if (data.section === 'body' && data.column.index >= 1 && data.column.index <= 5) {
          const val = parseInt(data.cell.text[0]) || 0;
          if (val > 0) {
            data.cell.styles.halign = 'center';
            if (data.column.index === 3) { // Approved
              data.cell.styles.textColor = [76, 175, 80];
              data.cell.styles.fontStyle = 'bold';
            } else if (data.column.index === 4) { // Rejected
              data.cell.styles.textColor = [244, 67, 54];
            } else if (data.column.index === 5) { // Review
              data.cell.styles.textColor = [33, 150, 243];
            }
          } else {
            data.cell.styles.halign = 'center';
            data.cell.styles.textColor = [200, 200, 200];
          }
        }
        // Center align level columns
        if (data.section === 'body' && data.column.index >= 6 && data.column.index <= 8) {
          data.cell.styles.halign = 'center';
        }
      }
    });

    drawFooter(1, Object.keys(teacherAgg).length + 1);

    /* ═══════════════════════════════════════════════════════════
       PAGES 2+: INDIVIDUAL TEACHER REPORTS
       ═══════════════════════════════════════════════════════════ */
    
    let pageNum = 2;
    Object.entries(teacherAgg).forEach(([teacher, stat]) => {
      doc.addPage('landscape');
      
      drawHeader(`Teacher Report: ${teacher}`);

      // Teacher info card
      const infoY = 45;
      drawCard(20, infoY, pageW - 40, 25, 'Teacher Information', [102, 126, 234]);
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(teacher, 25, infoY + 15);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Total Questions Created: ${stat.total}`, 25, infoY + 21);
      
      const teacherApprovalRate = stat.total > 0 
        ? ((stat.approved || 0) / stat.total * 100).toFixed(1) 
        : 0;
      doc.text(`Approval Rate: ${teacherApprovalRate}%`, 120, infoY + 21);
      
      const teacherPendingRate = stat.total > 0 
        ? ((stat.review_needed || 0) / stat.total * 100).toFixed(1) 
        : 0;
      doc.text(`Pending Review: ${teacherPendingRate}%`, 190, infoY + 21);
      
      doc.setTextColor(0, 0, 0);

      // Status breakdown
      const breakdownY = infoY + 35;
      drawCard(20, breakdownY, 130, 70, 'Question Status Breakdown', [33, 150, 243]);

      // Status table
      autoTable(doc, {
        head: [['Status', 'Count', 'Percentage']],
        body: statusCols.map((s) => {
          const count = stat[s] || 0;
          const percentage = stat.total > 0 ? ((count / stat.total) * 100).toFixed(1) + '%' : '0%';
          return [
            s === 'review_needed'
              ? 'Review Needed'
              : s.charAt(0).toUpperCase() + s.slice(1),
            count,
            percentage
          ];
        }),
        startY: breakdownY + 10,
        margin: { left: 25 },
        tableWidth: 60,
        theme: 'striped',
        headStyles: { 
          fillColor: [33, 150, 243],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: { 
          fontSize: 9,
          cellPadding: 2
        },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { halign: 'center', cellWidth: 15 },
          2: { halign: 'center', cellWidth: 15, textColor: [33, 150, 243], fontStyle: 'bold' }
        }
      });

      // Status chart
      const barUrl = chartToJpeg(
        'bar',
        {
          labels: [
            'Draft',
            'Submitted',
            'Approved',
            'Rejected',
            'Review Needed',
          ],
          datasets: [
            {
              label: 'Questions',
              data: statusCols.map((s) => stat[s] || 0),
              backgroundColor: [
                '#9E9E9E',
                '#FF9800',
                '#4CAF50',
                '#F44336',
                '#2196F3',
              ],
              borderRadius: 4,
            },
          ],
        },
        {
          plugins: { 
            legend: { display: false },
            title: {
              display: true,
              text: 'Visual Status Overview',
              font: { size: 11 }
            }
          },
          scales: { 
            y: { 
              beginAtZero: true, 
              ticks: { precision: 0, font: { size: 9 } },
              grid: { color: '#f0f0f0' }
            },
            x: {
              ticks: { font: { size: 9 } },
              grid: { display: false }
            }
          }
        },
        480,
        240,
      );
      doc.addImage(barUrl, 'JPEG', 95, breakdownY + 10, 85, 50);

      // Level distribution
      drawCard(195, breakdownY, 80, 70, 'Difficulty Distribution', [76, 175, 80]);

      autoTable(doc, {
        head: [['Level', 'Count', '%']],
        body: [
          ['Easy (1)', stat.L1 || 0, stat.total ? ((stat.L1 || 0) / stat.total * 100).toFixed(1) + '%' : '0%'],
          ['Medium (2)', stat.L2 || 0, stat.total ? ((stat.L2 || 0) / stat.total * 100).toFixed(1) + '%' : '0%'],
          ['Difficult (3)', stat.L3 || 0, stat.total ? ((stat.L3 || 0) / stat.total * 100).toFixed(1) + '%' : '0%'],
        ],
        startY: breakdownY + 10,
        margin: { left: 200 },
        tableWidth: 70,
        theme: 'striped',
        headStyles: { 
          fillColor: [76, 175, 80],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: { 
          fontSize: 9,
          cellPadding: 2.5
        },
        alternateRowStyles: { fillColor: [245, 250, 245] },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { halign: 'center', cellWidth: 18, fontSize: 10, fontStyle: 'bold' },
          2: { halign: 'center', cellWidth: 20, textColor: [76, 175, 80], fontStyle: 'bold' }
        }
      });

      // Level chart (doughnut)
      const levelDoughnutUrl = chartToJpeg(
        'doughnut',
        {
          labels: ['Easy', 'Medium', 'Difficult'],
          datasets: [
            {
              data: [stat.L1 || 0, stat.L2 || 0, stat.L3 || 0],
              backgroundColor: [
                '#4CAF50',
                '#FF9800',
                '#F44336',
              ],
              borderWidth: 2,
              borderColor: '#ffffff',
            },
          ],
        },
        {
          plugins: { 
            legend: { 
              display: true,
              position: 'bottom',
              labels: { 
                boxWidth: 10,
                font: { size: 8 },
                padding: 5
              }
            }
          },
          cutout: '65%'
        },
        280,
        200,
      );
      doc.addImage(levelDoughnutUrl, 'JPEG', 208, breakdownY + 38, 35, 28);

      drawFooter(pageNum, Object.keys(teacherAgg).length + 1);
      pageNum++;
    });

    doc.save('CBT_Dashboard_Report.pdf');
    } catch (err) {
      setFilterLoading(false);
      setError('Failed to generate PDF report. Please try again.');
      console.error('PDF generation error:', err);
    }
  };

  /* ───────── helpers ───────── */
  const handleView = (row) => {
    setSelectedQuestion(row);
    setOpenView(true);
  };
  const closeView = () => {
    setOpenView(false);
    setOpenSubModal(false);
    setSelectedQuestion(null);
  };
  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };



  /* ───────── Loading screen ───────── */
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-100px',
            right: '-100px',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            bottom: '-50px',
            left: '-50px',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 4,
              p: 5,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
            }}
          >
            {/* Animated Logo/Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 24px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.5px',
                }}
              >
                CBT
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#2c3e50',
                mb: 1,
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Loading Dashboard
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#7f8c8d',
                mb: 4,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Preparing your workspace...
            </Typography>

            {/* Modern Progress Bar */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e7ff',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>
            
            <Typography
              variant="body2"
              sx={{
                color: '#667eea',
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {progress}%
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  /* ───────── MAIN UI ───────── */
  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: { xs: 3, md: 4 },
          pb: 3,
          borderBottom: '3px solid',
          borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', mb: 0.5, fontSize: { xs: '1.75rem', sm: '2rem', md: '3rem' } }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Monitor and manage all questions across sessions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={buildPdf}
            fullWidth={true}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Generate&nbsp;Report
          </Button>
          {isSuperAdmin && (
            <Button
              variant="contained"
              onClick={() => navigate('/reset-password')}
              fullWidth={true}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  background: 'linear-gradient(135deg, #df7eea 0%, #e4465b 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(240, 147, 251, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              User&nbsp;Management
            </Button>
          )}
          <Button 
            variant="outlined" 
            onClick={logout}
            fullWidth={true}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              borderWidth: 2,
              borderColor: '#dc3545',
              color: '#dc3545',
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': {
                borderWidth: 2,
                borderColor: '#c82333',
                background: '#fff5f5',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            label: 'Submitted',
            icon: <HourglassEmpty sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            value: stats.submitted,
          },
          {
            label: 'Approved',
            icon: <CheckCircle sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            value: stats.approved,
          },
          {
            label: 'Rejected',
            icon: <Cancel sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            value: stats.rejected,
          },
          {
            label: 'Review Needed',
            icon: <ReportProblem sx={{ fontSize: 40 }} />,
            gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            value: stats.review,
          },
        ].map((c, i) => (
          <Grid key={i} item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                background: c.gradient,
                color: '#fff',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}>
                      {c.label}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {c.value}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {c.icon}
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total count
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* teacher */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Teacher</InputLabel>
          <Select
            label="Teacher"
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {teacherList.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* QA */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>QA</InputLabel>
          <Select
            label="QA"
            value={qaFilter}
            onChange={(e) => setQaFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {qaList.map((qa) => (
              <MenuItem key={qa._id} value={qa._id}>
                {qa.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* subject */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            label="Subject"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            {subjectList.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* status */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusList.map((s) => (
              <MenuItem key={s} value={s}>
                {s === 'review_needed'
                  ? 'Review Needed'
                  : s === 'All'
                  ? 'All'
                  : s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

            {/* level */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value={1}>Easy (1)</MenuItem>
            <MenuItem value={2}>Medium (2)</MenuItem>
            <MenuItem value={3}>Difficult (3)</MenuItem>
          </Select>
        </FormControl>

            {/* session */}
            <FormControl sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Session</InputLabel>
          <Select
            label="Session"
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {sessions.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Questions List ({pagination.total} total)
          </Typography>
        </Box>
        <Box sx={{ height: 550, width: '100%', position: 'relative' }}>
          {filterLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          )}
          <DataGrid
            rows={rows.map((q) => ({ ...q, questionSnippet: q.question }))}
            columns={columns}
            getRowId={(r) => r._id}
            paginationModel={{ page: pagination.page - 1, pageSize: 20 }}
            onPaginationModelChange={(model) => {
              if (model.page !== pagination.page - 1) {
                fetchQuestions(model.page + 1);
              }
            }}
            pageSizeOptions={[20]}
            slots={{ toolbar: GridToolbar }}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={pagination.total}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: '#f8f9ff',
              },
            }}
          />
        </Box>
      </Card>

      {/* Question Modal */}
      <Dialog 
        open={openView} 
        onClose={closeView} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Question Details
        </DialogTitle>
        {selectedQuestion && (
          <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
            {/* Info Cards Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #667eea',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Subject
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                    {selectedQuestion.subject}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #11998e',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Difficulty Level
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 700 }}>
                    {levelLabel(selectedQuestion.level)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #f093fb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Status
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: selectedQuestion.status === 'approved' ? '#4caf50' : 
                           selectedQuestion.status === 'rejected' ? '#f44336' : '#ff9800',
                    fontWeight: 700,
                    textTransform: 'capitalize'
                  }}>
                    {selectedQuestion.status === 'review_needed' ? 'Review Needed' : selectedQuestion.status}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  borderLeft: '4px solid #fa709a',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                    Teacher
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                    {selectedQuestion.teacherName || selectedQuestion.teacherId}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {selectedQuestion.qaName && (
              <Box sx={{ 
                bgcolor: 'white', 
                p: 2, 
                borderRadius: 2,
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
                  Assigned QA
                </Typography>
                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                  {selectedQuestion.qaName}
                </Typography>
              </Box>
            )}

            {selectedQuestion.reviewComment && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Review Comment:
                </Typography>
                <Typography variant="body2">
                  {selectedQuestion.reviewComment}
                </Typography>
              </Alert>
            )}

            {/* Question Text */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Question
              </Typography>
              {isSuperAdmin ? (
                <Box
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                    '& p': { marginBottom: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedQuestion.question }}
                />
              ) : (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2, 
                  border: '1px solid #e0e0e0',
                }}
                >
                  {(() => {
                    const fullText = (selectedQuestion.question || '').replace(/<[^>]+>/g, '');
                    const words = fullText.trim().split(/\s+/);
                    return words.slice(0, 3).join(' ') + (words.length > 3 ? ' .....' : '');
                  })()}
                </Box>
              )}
            </Box>

            {/* Options (hidden for admin-level users) */}
            {isSuperAdmin ? (
              selectedQuestion.options?.length > 0 && (
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 3, 
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                    Answer Options
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedQuestion.options.map((opt, i) => {
                      const isCorrect = opt === selectedQuestion.correctAnswer;
                      return (
                        <Grid item xs={12} sm={6} key={i}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                              bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <Box sx={{ 
                              minWidth: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}>
                              {String.fromCharCode(65 + i)}
                            </Box>
                            <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                              {isCorrect && (
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  color: '#4caf50',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                  ✓ Correct Answer
                                </Typography>
                              )}
                              <Box dangerouslySetInnerHTML={{ __html: opt }} />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )
            ) : (
              <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, mb: 2 }}>
                <Typography sx={{ color: '#64748b' }}>Answer options are hidden for Admin role.</Typography>
              </Box>
            )}

            {/* Sub Questions Button */}
            {selectedQuestion.subQuestions?.length > 0 && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setOpenSubModal(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View {selectedQuestion.subQuestions.length} Sub Question{selectedQuestion.subQuestions.length > 1 ? 's' : ''}
              </Button>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={closeView}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-questions Modal */}
      <Dialog
        open={openSubModal}
        onClose={() => setOpenSubModal(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.5rem',
          py: 2.5,
        }}>
          Sub Questions ({selectedQuestion?.subQuestions?.length || 0})
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          {selectedQuestion?.subQuestions?.map((sq, i) => (
            <Box 
              key={i} 
              sx={{ 
                bgcolor: 'white',
                borderRadius: 2,
                p: 3,
                mb: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #11998e',
              }}
            >
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: '#2c3e50',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  bgcolor: '#11998e',
                  color: 'white',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                }}>
                  {i + 1}
                </Box>
                Sub Question {i + 1}
              </Typography>
              
              <Box
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: sq.question }}
              />

              {sq.options?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1.5 }}>
                    Answer Options
                  </Typography>
                  <Grid container spacing={2}>
                    {sq.options.map((opt, j) => {
                      const isCorrect = opt === sq.correctAnswer;
                      return (
                        <Grid item xs={12} sm={6} key={j}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                              bgcolor: isCorrect ? '#e8f5e9' : '#fafafa',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ 
                              minWidth: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: isCorrect ? '#4caf50' : '#9e9e9e',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              flexShrink: 0,
                            }}>
                              {String.fromCharCode(65 + j)}
                            </Box>
                            <Box sx={{ flex: 1, '& img': { maxWidth: '100%', height: 'auto' } }}>
                              {isCorrect && (
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  color: '#4caf50',
                                  fontWeight: 700,
                                  mb: 0.5,
                                }}>
                                  ✓ Correct Answer
                                </Typography>
                              )}
                              <Box dangerouslySetInnerHTML={{ __html: opt }} />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={() => setOpenSubModal(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard;
